import { MessageSeverity, captureMessage } from "@/lib/monitoring/monitoring";
import { differenceInMinutes, differenceInSeconds, format } from "date-fns";
import { useEffect, useMemo, useState } from "react";

export function usePatientTimer(
  customerWaitingSince?: string,
  slaBreachTime?: string,
  context?: {
    patientId?: string;
    encounterId?: string;
  },
) {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const minutesSince = customerWaitingSince
      ? differenceInMinutes(new Date(), new Date(customerWaitingSince))
      : 0;

    const perSecond = 1000;
    const perMinute = perSecond * 60;
    const interval = minutesSince > 10 ? perMinute : perSecond; // only interval on as-need basis for re-rendering component

    const intervalId = setInterval(() => {
      setTime(new Date());
    }, interval);

    return () => clearInterval(intervalId);
  }, [customerWaitingSince]);

  // biome-ignore lint: time retriggering useMemo will recalculate the time ellapsed every n-seconds
  const customerWaitingTimer = useMemo(() => {
    if (!customerWaitingSince) {
      // Log to Sentry when customerWaitingSince is undefined
      captureMessage(
        "Patient timer encountered undefined customerWaitingSince",
        MessageSeverity.Warning,
        {
          patientId: context?.patientId,
          encounterId: context?.encounterId,
          slaBreachTime,
        },
      );

      return "Unknown Wait Time";
    }

    const { days, hours, minutes, seconds, weeks } =
      ellapsedTimeSince(customerWaitingSince);

    if (weeks >= 1) {
      return format(new Date(customerWaitingSince), "MM/dd/yyyy");
    }

    if (days >= 1) {
      return `${days} days`;
    }

    if (hours >= 1) {
      if (minutes > 0) return `${hours}hr${minutes}m`;
      return `${hours}hr`;
    }

    if (minutes >= 10) {
      return `${minutes}m`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, [time, customerWaitingSince]);

  const hasSLABreached = useMemo(() => {
    if (!slaBreachTime) return false;

    return time.toISOString() > slaBreachTime;
  }, [time, slaBreachTime]);

  return {
    customerWaitingTimer,
    hasSLABreached,
  };
}

const ellapsedTimeSince = (customerWaitingSince: string) => {
  const now = new Date();
  const time = new Date(customerWaitingSince);

  const secondsDifference = differenceInSeconds(now, time);

  const daysTotal = Math.floor(secondsDifference / 86400);
  const weeks = Math.floor(daysTotal / 7);
  const days = daysTotal % 7;
  const hours = Math.floor((secondsDifference % 86400) / 3600);
  const minutes = Math.floor((secondsDifference % 3600) / 60);
  const seconds = secondsDifference % 60;

  return {
    weeks,
    days,
    hours,
    minutes,
    seconds,
  };
};
